import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import { generateMathQuestions } from './questions/math';
import SCIENCE  from './questions/science';
import ENGLISH  from './questions/english';
import SOCIAL   from './questions/social';
import FUNFACTS from './questions/funfacts';
import GEO      from './questions/geography';

// ── Subject Config ────────────────────────────────────────────────────────────
const MASCOT_IMG = 'https://images.unsplash.com/photo-1543549049-9fbcc3cd9003?w=320&h=320&fit=crop&auto=format';

const SUBJECTS = [
  { key:'Math',           image:'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400&h=260&fit=crop&auto=format', gradient:'from-blue-500 to-cyan-500',     bg:'bg-blue-50',   border:'border-blue-300',   text:'text-blue-700',   desc:'Numbers & Problem Solving'      },
  { key:'Science',        image:'https://images.unsplash.com/photo-1532094349884-543290af57a0?w=400&h=260&fit=crop&auto=format', gradient:'from-emerald-500 to-green-600', bg:'bg-green-50',  border:'border-green-300',  text:'text-green-700',  desc:'Nature & The World Around Us'   },
  { key:'English',        image:'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=400&h=260&fit=crop&auto=format', gradient:'from-purple-500 to-violet-600', bg:'bg-purple-50', border:'border-purple-300', text:'text-purple-700', desc:'Reading, Writing & Grammar'      },
  { key:'Social Studies', image:'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=400&h=260&fit=crop&auto=format', gradient:'from-orange-500 to-amber-500',  bg:'bg-orange-50', border:'border-orange-300', text:'text-orange-700', desc:'People, Places & History'        },
  { key:'Fun Facts',      image:'https://images.unsplash.com/photo-1513258496099-48168024aec0?w=400&h=260&fit=crop&auto=format', gradient:'from-pink-500 to-rose-500',     bg:'bg-pink-50',   border:'border-pink-300',   text:'text-pink-700',   desc:'Amazing Trivia & Cool Facts'    },
  { key:'Geography',      image:'https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?w=400&h=260&fit=crop&auto=format', gradient:'from-teal-500 to-cyan-600',     bg:'bg-teal-50',   border:'border-teal-300',   text:'text-teal-700',   desc:'Maps, Countries & Landmarks'    },
];

const GRADE_LABELS = ['Kindergarten','1st Grade','2nd Grade','3rd Grade','4th Grade','5th Grade'];
const TOTAL_LEVELS = 10;
const QS_PER_LEVEL = 10;

const CORRECT_MSGS = ['Awesome, {name}! 🌟','You got it! 🎉','Brilliant! ✨','Superstar! ⭐','Way to go, {name}! 🚀','Fantastic! 🏆','You\'re on fire! 🔥','Perfect! 💎','Outstanding! 🎊','Genius, {name}! 🧠'];
const WRONG_MSGS   = ['Nice try, {name}! 💪','Good effort! You\'ve got this! 🌈','Don\'t worry — learning takes practice! 🤗','Keep going, {name}! 👍','Almost there! 🎯','Every mistake helps you grow! 🌱'];

// ── Helpers ───────────────────────────────────────────────────────────────────
function shuffle(arr) { return [...arr].sort(() => Math.random() - 0.5); }
function pickMsg(arr, name) { return arr[Math.floor(Math.random()*arr.length)].replace(/\{name\}/g, name); }

function getQuestions(subject, level) {
  if (subject === 'Math') return generateMathQuestions(level);
  const banks = { Science: SCIENCE, English: ENGLISH, 'Social Studies': SOCIAL, 'Fun Facts': FUNFACTS, Geography: GEO };
  const pool = banks[subject].filter(q => q.l === level);
  return shuffle(pool).slice(0, QS_PER_LEVEL);
}

function playTone(correct) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    if (correct) {
      osc.type = 'sine';
      osc.frequency.setValueAtTime(523, ctx.currentTime);
      osc.frequency.setValueAtTime(659, ctx.currentTime + 0.1);
      osc.frequency.setValueAtTime(784, ctx.currentTime + 0.2);
      gain.gain.setValueAtTime(0.22, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
    } else {
      osc.type = 'sine';
      osc.frequency.setValueAtTime(280, ctx.currentTime);
      osc.frequency.setValueAtTime(180, ctx.currentTime + 0.2);
      gain.gain.setValueAtTime(0.18, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
    }
    osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.7);
  } catch(_) {}
}

// ── Image with fallback ───────────────────────────────────────────────────────
function ImgWithFallback({ src, alt, className, style, fallbackStyle }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <div className={className} style={{ ...fallbackStyle, ...style }} aria-label={alt} />;
  return <img src={src} alt={alt} className={className} style={style} onError={() => setFailed(true)} />;
}

// ── Confetti ──────────────────────────────────────────────────────────────────
function Confetti({ count = 20 }) {
  const pieces = ['⭐','🎉','✨','🌟','💫','🎊','🌈','💎','🎈','🏆','🎯','🔥'];
  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {Array.from({ length: count }, (_, i) => (
        <span key={i} className="absolute text-2xl animate-floatUp" style={{
          left: `${3 + (i * 4.9) % 94}%`,
          top: `${40 + (i % 5) * 8}%`,
          animationDelay: `${(i * 0.055).toFixed(2)}s`,
          animationDuration: `${0.8 + (i % 5) * 0.2}s`,
        }}>{pieces[i % pieces.length]}</span>
      ))}
    </div>
  );
}

// ── Floating background decoration ───────────────────────────────────────────
function FloatingDecor() {
  const shapes = [
    { bg:'#fbbf24', size:48, radius:'50%',  rotate:0  },
    { bg:'#60a5fa', size:28, radius:'50%',  rotate:0  },
    { bg:'#f472b6', size:36, radius:'8px',  rotate:45 },
    { bg:'#a78bfa', size:56, radius:'50%',  rotate:0  },
    { bg:'#34d399', size:40, radius:'12px', rotate:20 },
    { bg:'#fb923c', size:22, radius:'50%',  rotate:0  },
    { bg:'#f9a8d4', size:50, radius:'8px',  rotate:30 },
    { bg:'#67e8f9', size:34, radius:'50%',  rotate:0  },
    { bg:'#fde68a', size:30, radius:'50%',  rotate:0  },
    { bg:'#c084fc', size:44, radius:'12px', rotate:60 },
  ];
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0 opacity-20">
      {shapes.map((s, i) => (
        <div key={i} className="absolute animate-pulse2" style={{
          left: `${(i * 11.3) % 95}%`,
          top: `${(i * 17.7) % 90}%`,
          width: s.size, height: s.size,
          background: s.bg,
          borderRadius: s.radius,
          transform: `rotate(${s.rotate}deg)`,
          animationDelay: `${(i * 0.4).toFixed(1)}s`,
          animationDuration: `${3 + (i % 3)}s`,
        }}/>
      ))}
    </div>
  );
}

// ── Welcome Screen ────────────────────────────────────────────────────────────
function WelcomeScreen({ onStart }) {
  const [name, setName]   = useState('');
  const [grade, setGrade] = useState('');
  const canGo = name.trim() && grade;

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-200 via-pink-100 to-blue-200 flex items-center justify-center p-4 font-baloo relative overflow-hidden">
      <FloatingDecor />
      <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center animate-slideIn relative z-10">
        {/* Mascot */}
        <div className="relative inline-block mb-2">
          <ImgWithFallback
            src={MASCOT_IMG}
            alt="Ollie the Owl mascot"
            className="w-36 h-36 rounded-full object-cover animate-wiggle shadow-xl border-4 border-purple-200"
            fallbackStyle={{background:'linear-gradient(135deg,#7c3aed,#a855f7)',borderRadius:'50%'}}
          />
        </div>
        {/* Title */}
        <h1 className="text-5xl font-extrabold mb-1 leading-tight" style={{background:'linear-gradient(135deg,#7c3aed,#ec4899,#f97316)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>
          LearnLand!
        </h1>
        <p className="text-lg font-bold text-yellow-500 mb-1">10 Levels · 100 Questions · 6 Subjects!</p>
        <p className="text-gray-400 text-sm mb-6 font-medium">Tell us about yourself to begin your adventure!</p>

        <div className="space-y-4 text-left">
          <div>
            <label className="block text-base font-bold text-purple-700 mb-1">What's your name? 😊</label>
            <input
              type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder="Type your name here..." maxLength={30}
              className="w-full rounded-2xl px-4 py-3 text-xl font-baloo focus:outline-none focus:ring-4 focus:ring-purple-200 transition"
              style={{border:'3px solid #c084fc'}}
            />
          </div>
          <div>
            <label className="block text-base font-bold text-pink-600 mb-1">What grade are you in? 🎓</label>
            <select value={grade} onChange={e => setGrade(e.target.value)}
              className="w-full rounded-2xl px-4 py-3 text-xl font-baloo focus:outline-none focus:ring-4 focus:ring-pink-200 transition bg-white cursor-pointer appearance-none"
              style={{border:'3px solid #f9a8d4'}}
            >
              <option value="">-- Pick your grade --</option>
              {GRADE_LABELS.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
        </div>

        <button onClick={() => canGo && onStart(name.trim(), grade)} disabled={!canGo}
          className={`mt-7 w-full py-4 rounded-full text-white text-2xl font-extrabold shadow-xl transition-all duration-200 ${canGo ? 'cursor-pointer hover:scale-105 hover:shadow-2xl animate-pulse2' : 'opacity-50 cursor-not-allowed'}`}
          style={canGo ? {background:'linear-gradient(135deg,#7c3aed,#ec4899)'} : {background:'#d1d5db'}}
        >
          Let's Go!
        </button>
        <p className="mt-3 text-gray-400 text-sm">Ollie the Owl is excited to teach you!</p>
      </div>
    </div>
  );
}

// ── Subject Picker ────────────────────────────────────────────────────────────
function SubjectPicker({ name, grade, totalXP, onSelect, onReset }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-200 via-purple-100 to-yellow-200 p-5 font-baloo relative overflow-hidden">
      <FloatingDecor />
      <div className="max-w-2xl mx-auto relative z-10 animate-slideIn">
        {/* Header */}
        <div className="text-center mb-6">
          <ImgWithFallback
            src={MASCOT_IMG}
            alt="Ollie the Owl"
            className="w-20 h-20 rounded-full object-cover animate-bounce inline-block shadow-lg border-4 border-purple-200 mb-2"
            fallbackStyle={{background:'linear-gradient(135deg,#7c3aed,#a855f7)',borderRadius:'50%'}}
          />
          <h2 className="text-3xl font-extrabold text-purple-700">Hi, {name}!</h2>
          <p className="text-base text-gray-600 font-semibold mt-1">
            {grade} · <span className="text-yellow-600">{totalXP} XP total</span>
          </p>
          <p className="text-lg text-purple-500 font-bold mt-1">Which subject shall we explore?</p>
        </div>

        {/* Subject grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-5">
          {SUBJECTS.map(sub => (
            <button key={sub.key} onClick={() => onSelect(sub.key)}
              className={`${sub.bg} border-2 ${sub.border} rounded-3xl text-center shadow-md hover:shadow-2xl hover:scale-105 transition-all duration-200 cursor-pointer group relative overflow-hidden`}
            >
              <div className={`absolute inset-0 opacity-0 group-hover:opacity-10 bg-gradient-to-br ${sub.gradient} transition-opacity duration-200`}/>
              <ImgWithFallback
                src={sub.image}
                alt={sub.key}
                className="w-full h-28 object-cover rounded-t-3xl"
                fallbackStyle={{background:`linear-gradient(135deg,var(--tw-gradient-stops))`,height:'112px',borderRadius:'24px 24px 0 0'}}
              />
              <div className="px-3 py-2">
                <div className={`text-base font-extrabold ${sub.text} leading-tight`}>{sub.key}</div>
                <p className="text-gray-400 text-xs mt-0.5 font-medium">{sub.desc}</p>
                <p className="text-purple-400 text-xs mt-0.5 font-bold">10 Levels →</p>
              </div>
            </button>
          ))}
        </div>

        <div className="text-center">
          <button onClick={onReset} className="text-gray-400 hover:text-gray-600 text-sm underline transition font-medium">
            ✏️ Change name or grade
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Level Map ─────────────────────────────────────────────────────────────────
function LevelMap({ subject, currentLevel, onSelectLevel, onBack }) {
  const sub = SUBJECTS.find(s => s.key === subject);
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-200 via-purple-100 to-pink-200 p-5 font-baloo">
      <div className="max-w-xl mx-auto animate-slideIn">
        <button onClick={onBack} className="text-purple-500 hover:text-purple-700 font-bold text-lg mb-4 flex items-center gap-1 transition">
          ← Back
        </button>
        <div className="text-center mb-6">
          {sub && (
            <ImgWithFallback
              src={sub.image}
              alt={subject}
              className="w-24 h-24 rounded-2xl object-cover mx-auto mb-2 shadow-lg"
              fallbackStyle={{background:'linear-gradient(135deg,#7c3aed,#ec4899)',borderRadius:'16px',height:'96px'}}
            />
          )}
          <h2 className="text-3xl font-extrabold text-purple-700">{subject}</h2>
          <p className="text-gray-500 font-semibold mt-1">Choose your level!</p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {Array.from({length:TOTAL_LEVELS},(_,i)=>i+1).map(lvl => {
            const unlocked = lvl <= currentLevel;
            const isCurrent = lvl === currentLevel;
            const done = lvl < currentLevel;
            return (
              <button key={lvl} onClick={() => unlocked && onSelectLevel(lvl)} disabled={!unlocked}
                className={`rounded-2xl p-5 text-center shadow-md transition-all duration-200 border-2
                  ${unlocked ? 'cursor-pointer hover:scale-105 hover:shadow-xl' : 'cursor-not-allowed opacity-50'}
                  ${isCurrent ? 'bg-yellow-50 border-yellow-400 scale-105 shadow-xl' : done ? 'bg-green-50 border-green-400' : 'bg-gray-50 border-gray-200'}`}
              >
                <div className="text-3xl mb-1">{done ? '✅' : isCurrent ? '🎯' : unlocked ? '🔓' : '🔒'}</div>
                <div className={`text-lg font-extrabold ${isCurrent ? 'text-yellow-700' : done ? 'text-green-700' : 'text-gray-500'}`}>
                  Level {lvl}
                </div>
                <div className="text-xs text-gray-400 font-medium mt-1">{QS_PER_LEVEL} questions</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Level-Up Screen ───────────────────────────────────────────────────────────
function LevelUpScreen({ name, level, score, xpEarned, onContinue, onBack }) {
  const pct = Math.round((score / QS_PER_LEVEL) * 100);
  const stars = score >= 9 ? 3 : score >= 7 ? 2 : score >= 5 ? 1 : 0;
  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-100 via-orange-100 to-pink-200 flex items-center justify-center p-4 font-baloo">
      <Confetti count={30} />
      <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-sm w-full text-center animate-bounceIn">
        <div className="text-6xl mb-2 animate-bounce">{level >= TOTAL_LEVELS ? '🏆' : '⬆️'}</div>
        <h2 className="text-3xl font-extrabold text-purple-700 mb-1">
          {level >= TOTAL_LEVELS ? 'Subject Complete!' : `Level ${level} Done!`}
        </h2>
        <p className="text-gray-500 font-medium mb-4">{name}, you crushed it! 🎉</p>

        {/* Stars */}
        <div className="text-4xl mb-3">
          {[1,2,3].map(s => <span key={s} className={s <= stars ? 'opacity-100' : 'opacity-20'}>⭐</span>)}
        </div>

        {/* Score */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-4 mb-4 border-2 border-purple-100">
          <p className="text-4xl font-extrabold text-purple-700">{score}<span className="text-gray-400 text-2xl">/{QS_PER_LEVEL}</span></p>
          <p className="text-lg font-bold text-pink-500">{pct}% Correct</p>
          <p className="text-sm font-bold text-yellow-600 mt-1">+{xpEarned} XP earned! ⭐</p>
        </div>

        <div className="flex flex-col gap-3">
          {level < TOTAL_LEVELS && (
            <button onClick={onContinue}
              className="py-4 rounded-full text-white text-xl font-extrabold shadow-lg hover:scale-105 transition"
              style={{background:'linear-gradient(135deg,#7c3aed,#ec4899)'}}
            >
              Next Level → (Level {level + 1})
            </button>
          )}
          <button onClick={onBack}
            className="py-3 rounded-full text-white font-bold text-lg shadow-md hover:scale-105 transition"
            style={{background:'linear-gradient(135deg,#3b82f6,#06b6d4)'}}
          >
            {level >= TOTAL_LEVELS ? '🎯 Try Another Subject' : '📚 Subject Menu'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Learning Module ───────────────────────────────────────────────────────────
function LearningModule({ name, subject, level, onLevelComplete, onBack }) {
  const [questions]       = useState(() => getQuestions(subject, level));
  const [idx, setIdx]     = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [selected, setSelected] = useState(null);
  const [fillVal, setFillVal]   = useState('');
  const [shake, setShake]       = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [mcOptions, setMcOptions] = useState([]);
  const timerRef = useRef(null);
  const inputRef = useRef(null);
  const sub = SUBJECTS.find(s => s.key === subject);
  const q = questions[idx];

  useEffect(() => {
    if (q?.t === 'mc') setMcOptions(shuffle(q.o));
  }, [idx, q]);

  useEffect(() => {
    if (q?.t === 'fill' && inputRef.current) inputRef.current.focus();
  }, [idx, q]);

  const advance = useCallback((addPoint) => {
    const newScore = score + (addPoint ? 1 : 0);
    if (idx + 1 >= questions.length) {
      onLevelComplete(newScore);
    } else {
      setIdx(i => i + 1);
      setFeedback(null); setAnswered(false);
      setSelected(null); setFillVal('');
      setShake(false); setShowConfetti(false);
    }
  }, [idx, questions.length, onLevelComplete, score]);

  const submitAnswer = useCallback((userAnswer) => {
    if (answered) return;
    const correct = userAnswer.trim().toLowerCase() === q.a.trim().toLowerCase();
    setAnswered(true); setSelected(userAnswer);
    const newStreak = correct ? streak + 1 : 0;
    setStreak(newStreak);
    setFeedback({ correct, msg: pickMsg(correct ? CORRECT_MSGS : WRONG_MSGS, name), explanation: q.e, correctAnswer: q.a });
    if (correct) { setScore(s => s+1); setShowConfetti(true); playTone(true); }
    else { setShake(true); playTone(false); setTimeout(() => setShake(false), 600); }
    timerRef.current = setTimeout(() => advance(correct), correct ? 3000 : 3800);
  }, [answered, q, name, streak, advance]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  if (!q) return null;
  const progress = (idx / questions.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100 p-4 font-baloo">
      {showConfetti && <Confetti count={16} />}
      <div className="max-w-2xl mx-auto">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-3 pt-1">
          <button onClick={onBack} className="text-purple-500 hover:text-purple-700 font-bold text-base transition flex items-center gap-1">
            ← Back
          </button>
          <div className="flex items-center gap-2">
            {sub && (
              <ImgWithFallback
                src={sub.image}
                alt={subject}
                className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
                fallbackStyle={{background:'linear-gradient(135deg,#7c3aed,#ec4899)',borderRadius:'8px',flexShrink:'0'}}
              />
            )}
            <span className="text-base font-extrabold text-purple-700">{subject}</span>
            <span className="text-xs font-bold text-purple-400 bg-purple-50 rounded-full px-2 py-0.5 border border-purple-200">Lv.{level}</span>
          </div>
          <div className="flex items-center gap-2">
            {streak >= 3 && <span className="text-orange-500 font-extrabold text-sm animate-pulse2">🔥{streak}</span>}
            <span className="font-bold text-sm text-yellow-600 bg-yellow-50 rounded-full px-2 py-0.5 border border-yellow-200">⭐{score}/{idx}</span>
          </div>
        </div>

        {/* Progress + question counter */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-gray-400 mb-1 font-semibold">
            <span>Question {idx+1} of {questions.length} · Level {level}/{TOTAL_LEVELS}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden shadow-inner">
            <div className="h-3 rounded-full transition-all duration-700" style={{width:`${progress}%`,background:'linear-gradient(90deg,#7c3aed,#ec4899)'}}/>
          </div>
          {/* Level dots */}
          <div className="flex gap-1 mt-2 justify-center">
            {Array.from({length:TOTAL_LEVELS},(_,i)=>i+1).map(l => (
              <div key={l} className={`h-1.5 rounded-full transition-all duration-300 ${l < level ? 'bg-green-400' : l === level ? 'bg-purple-500' : 'bg-gray-200'}`} style={{width: l===level?'24px':'8px'}}/>
            ))}
          </div>
        </div>

        {/* Streak banner */}
        {streak >= 3 && (
          <div className="mb-3 bg-orange-50 border-2 border-orange-200 rounded-2xl px-4 py-2 text-center animate-bounceIn">
            <span className="text-orange-600 font-extrabold text-sm">🔥 {streak} in a row! You're on a streak!</span>
          </div>
        )}

        {/* Question card */}
        <div key={idx} className={`bg-white rounded-3xl shadow-xl p-6 mb-4 ${shake ? 'animate-shake' : 'animate-slideIn'}`}>
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-gray-400 bg-gray-100 rounded-full px-3 py-1 mb-3">
            {q.t === 'mc' ? '🔵 Multiple Choice' : q.t === 'tf' ? '⚡ True or False' : '✏️ Fill in the Blank'}
          </span>
          <h3 className="text-2xl font-bold text-gray-800 leading-snug mb-5">{q.q}</h3>

          {/* Multiple choice */}
          {q.t === 'mc' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {mcOptions.map((opt, i) => {
                let cls = 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-purple-50 hover:border-purple-400 hover:text-purple-800 hover:scale-102';
                if (answered) {
                  if (opt === q.a) cls = 'bg-green-100 border-green-500 text-green-800 scale-105 shadow-md';
                  else if (opt === selected) cls = 'bg-red-100 border-red-400 text-red-700';
                  else cls = 'bg-gray-50 border-gray-100 text-gray-300 opacity-40';
                }
                return (
                  <button key={i} onClick={() => !answered && submitAnswer(opt)} disabled={answered}
                    className={`border-2 rounded-2xl px-4 py-4 text-lg font-bold text-left transition-all duration-150 ${cls} ${!answered ? 'cursor-pointer active:scale-95' : 'cursor-default'}`}
                  >
                    {answered && opt === q.a && <span className="mr-1">✅</span>}
                    {answered && opt === selected && opt !== q.a && <span className="mr-1">❌</span>}
                    {opt}
                  </button>
                );
              })}
            </div>
          )}

          {/* True / False */}
          {q.t === 'tf' && (
            <div className="grid grid-cols-2 gap-4">
              {['True','False'].map(opt => {
                let cls = opt==='True' ? 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100 hover:scale-105' : 'bg-red-50 border-red-300 text-red-700 hover:bg-red-100 hover:scale-105';
                if (answered) {
                  if (opt === q.a) cls = 'bg-green-200 border-green-600 text-green-900 scale-105 shadow-md font-extrabold';
                  else if (opt === selected) cls = 'bg-red-200 border-red-500 text-red-900';
                  else cls = 'opacity-40';
                }
                return (
                  <button key={opt} onClick={() => !answered && submitAnswer(opt)} disabled={answered}
                    className={`border-2 rounded-2xl py-6 text-2xl font-extrabold text-center transition-all duration-150 ${cls} ${!answered ? 'cursor-pointer active:scale-95' : 'cursor-default'}`}
                  >
                    {opt === 'True' ? '✅ True' : '❌ False'}
                  </button>
                );
              })}
            </div>
          )}

          {/* Fill in blank */}
          {q.t === 'fill' && (
            <div>
              <input ref={inputRef} type="text" value={fillVal}
                onChange={e => !answered && setFillVal(e.target.value)}
                onKeyDown={e => e.key==='Enter' && !answered && fillVal.trim() && submitAnswer(fillVal)}
                disabled={answered} placeholder="Type your answer and press Enter..."
                className="w-full rounded-2xl px-5 py-4 text-2xl font-bold text-center focus:outline-none focus:ring-4 focus:ring-purple-200 transition"
                style={{border:`3px solid ${answered ? (selected?.toLowerCase()===q.a.toLowerCase() ? '#22c55e':'#ef4444') : '#a855f7'}`, background: answered ? '#f9fafb':'white'}}
              />
              {!answered && (
                <button onClick={() => fillVal.trim() && submitAnswer(fillVal)} disabled={!fillVal.trim()}
                  className="mt-3 w-full py-4 rounded-full text-white text-xl font-extrabold shadow-lg hover:scale-105 transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  style={{background:'linear-gradient(135deg,#7c3aed,#ec4899)'}}
                >
                  Check Answer ✓
                </button>
              )}
            </div>
          )}
        </div>

        {/* Feedback */}
        {feedback && (
          <div className={`rounded-3xl p-5 animate-bounceIn shadow-lg ${feedback.correct ? 'bg-green-50 border-2 border-green-300' : 'bg-orange-50 border-2 border-orange-300'}`}>
            <div className="flex items-start gap-3">
              <span className="text-4xl flex-shrink-0">{feedback.correct ? '🎉' : '💪'}</span>
              <div className="flex-1">
                <p className={`text-xl font-extrabold ${feedback.correct ? 'text-green-700':'text-orange-700'}`}>{feedback.msg}</p>
                {!feedback.correct && (
                  <p className="text-gray-600 font-semibold mt-1">
                    ✅ Answer: <span className="text-green-700 font-extrabold">{feedback.correctAnswer}</span>
                  </p>
                )}
                <p className="text-gray-600 text-sm mt-2 leading-relaxed">{feedback.explanation}</p>
              </div>
            </div>
            <button onClick={() => { clearTimeout(timerRef.current); advance(feedback.correct); }}
              className="mt-4 w-full py-3 rounded-full text-white font-bold text-lg shadow-md hover:scale-105 transition"
              style={{background:'linear-gradient(135deg,#3b82f6,#7c3aed)'}}
            >
              {idx + 1 >= questions.length ? 'See Results! 🏆' : 'Next Question →'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Final Results (Subject Complete) ─────────────────────────────────────────
function FinalResults({ name, subject, grade, totalScore, totalXP, onPickSubject, onReset }) {
  const pct = Math.round((totalScore / (TOTAL_LEVELS * QS_PER_LEVEL)) * 100);
  let badge, badgeMsg;
  if (pct === 100)    { badge = '🏆'; badgeMsg = 'LEGENDARY! Perfect 100! You\'re a LearnLand Champion!'; }
  else if (pct >= 80) { badge = '🥇'; badgeMsg = 'Outstanding! Gold Star performance!'; }
  else if (pct >= 60) { badge = '🥈'; badgeMsg = 'Great work! You\'ve leveled up your knowledge!'; }
  else if (pct >= 40) { badge = '🥉'; badgeMsg = 'Good effort! Every level teaches you something!'; }
  else                { badge = '💪'; badgeMsg = 'Great start! Practice makes perfect!'; }

  const handlePrint = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><title>LearnLand Certificate — ${name}</title>
    <link href="https://fonts.googleapis.com/css2?family=Baloo+2:wght@700;800&display=swap" rel="stylesheet"/>
    <style>*{box-sizing:border-box}body{font-family:'Baloo 2',sans-serif;background:linear-gradient(135deg,#fef9c3,#fce7f3,#dbeafe);min-height:100vh;display:flex;align-items:center;justify-content:center;padding:20px;margin:0}
    .cert{background:white;border-radius:24px;padding:48px;max-width:640px;width:100%;text-align:center;box-shadow:0 20px 60px rgba(0,0,0,.15);border:5px solid #a855f7}
    h1{font-size:2rem;font-weight:800;background:linear-gradient(to right,#7c3aed,#ec4899,#f97316);-webkit-background-clip:text;-webkit-text-fill-color:transparent;margin-bottom:8px}
    .badge{font-size:5rem;margin:16px 0;display:block}.name{font-size:2.5rem;font-weight:800;color:#7c3aed;margin:8px 0}
    .detail{font-size:1.1rem;color:#374151;margin:6px 0}.score-box{background:linear-gradient(to right,#f3e8ff,#fce7f3);border-radius:16px;padding:20px;margin:20px 0}
    .score{font-size:2.5rem;font-weight:800;color:#7c3aed}.pct{font-size:1.4rem;font-weight:700;color:#ec4899}.xp{font-size:1.1rem;color:#d97706;font-weight:700;margin-top:4px}
    .msg{font-size:1.3rem;font-weight:700;color:#1f2937;margin:16px 0}.footer{margin-top:24px;font-size:.85rem;color:#9ca3af}
    @media print{body{background:white}.cert{box-shadow:none}}</style></head>
    <body><div class="cert">
    <h1>🚀 LearnLand Certificate of Achievement</h1>
    <div class="badge">${badge}</div>
    <div class="detail">This certificate is proudly awarded to</div>
    <div class="name">${name}</div>
    <div class="detail">for completing ALL 10 LEVELS of <strong>${subject}</strong></div>
    <div class="detail">as a proud <strong>${grade}</strong> student!</div>
    <div class="score-box">
      <div class="score">${totalScore} / ${TOTAL_LEVELS * QS_PER_LEVEL}</div>
      <div class="pct">${pct}% Overall Score</div>
      <div class="xp">⭐ ${totalXP} XP Earned!</div>
    </div>
    <div style="font-size:2rem">⭐⭐⭐⭐⭐</div>
    <div class="msg">${badgeMsg}</div>
    <div class="footer">LearnLand · ${new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}<br>🦉 Keep learning, keep growing!</div>
    </div></body></html>`);
    w.document.close(); setTimeout(() => w.print(), 600);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-200 via-pink-100 to-blue-200 flex items-center justify-center p-4 font-baloo">
      <Confetti count={35}/>
      <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center animate-bounceIn">
        <div className="text-7xl mb-2 animate-bounce inline-block">{badge}</div>
        <h2 className="text-3xl font-extrabold text-purple-700 mb-1">Subject Complete! 🎉</h2>
        <p className="text-gray-500 font-medium mb-4">{subject} · All 10 Levels!</p>

        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-5 mb-4 border-2 border-purple-100">
          <p className="text-5xl font-extrabold text-purple-700">{totalScore}<span className="text-gray-400 text-2xl">/{TOTAL_LEVELS * QS_PER_LEVEL}</span></p>
          <p className="text-xl font-bold text-pink-500 mt-1">{pct}% Overall!</p>
          <p className="text-sm font-bold text-yellow-600 mt-1">⭐ {totalXP} XP earned this session!</p>
          <div className="w-full bg-gray-200 rounded-full h-3 mt-3 overflow-hidden">
            <div className="h-3 rounded-full transition-all duration-1000" style={{width:`${pct}%`,background:'linear-gradient(90deg,#22c55e,#10b981)'}}/>
          </div>
        </div>
        <p className="text-lg font-bold text-gray-700 mb-5 leading-snug">{badgeMsg}</p>
        <div className="flex flex-col gap-3">
          <button onClick={onPickSubject} className="py-4 rounded-full text-white text-xl font-extrabold shadow-lg hover:scale-105 transition" style={{background:'linear-gradient(135deg,#7c3aed,#ec4899)'}}>
            📚 Try Another Subject
          </button>
          <button onClick={handlePrint} className="py-4 rounded-full text-white text-xl font-extrabold shadow-lg hover:scale-105 transition" style={{background:'linear-gradient(135deg,#f59e0b,#f97316)'}}>
            🖨️ Print Certificate
          </button>
          <button onClick={onReset} className="py-3 rounded-full text-gray-500 font-bold border-2 border-gray-200 hover:border-gray-400 hover:text-gray-700 transition">
            👤 Change Player
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  const [screen,  setScreen]  = useState('welcome');  // welcome | subjects | levelmap | learning | levelup | final
  const [name,    setName]    = useState('');
  const [grade,   setGrade]   = useState('');
  const [subject, setSubject] = useState(null);
  const [level,   setLevel]   = useState(1);
  const [levelScore, setLevelScore] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [totalXP,    setTotalXP]    = useState(0);
  const [unlockedLevels, setUnlockedLevels] = useState({}); // { Math: 3, Science: 1 ... }

  const getMaxLevel = (sub) => unlockedLevels[sub] || 1;

  return (
    <div className="font-baloo">
      {screen === 'welcome' && (
        <WelcomeScreen onStart={(n,g) => { setName(n); setGrade(g); setScreen('subjects'); }} />
      )}
      {screen === 'subjects' && (
        <SubjectPicker name={name} grade={grade} totalXP={totalXP}
          onSelect={sub => {
            setSubject(sub);
            setTotalScore(0);
            setScreen('levelmap');
          }}
          onReset={() => setScreen('welcome')}
        />
      )}
      {screen === 'levelmap' && (
        <LevelMap subject={subject} currentLevel={getMaxLevel(subject)}
          onSelectLevel={lvl => { setLevel(lvl); setScreen('learning'); }}
          onBack={() => setScreen('subjects')}
        />
      )}
      {screen === 'learning' && (
        <LearningModule key={`${subject}-lv${level}-${Date.now()}`}
          name={name} subject={subject} level={level}
          onLevelComplete={score => {
            const xp = score * 10;
            setLevelScore(score);
            setTotalScore(ts => ts + score);
            setTotalXP(tx => tx + xp);
            // Unlock next level
            const nextLevel = Math.min(level + 1, TOTAL_LEVELS);
            setUnlockedLevels(prev => ({...prev, [subject]: Math.max(prev[subject]||1, nextLevel)}));
            setScreen('levelup');
          }}
          onBack={() => setScreen('levelmap')}
        />
      )}
      {screen === 'levelup' && (
        <LevelUpScreen name={name} level={level} score={levelScore} xpEarned={levelScore * 10}
          onContinue={() => { setLevel(l => l + 1); setScreen('learning'); }}
          onBack={() => {
            if (level >= TOTAL_LEVELS) setScreen('final');
            else setScreen('levelmap');
          }}
        />
      )}
      {screen === 'final' && (
        <FinalResults name={name} subject={subject} grade={grade}
          totalScore={totalScore} totalXP={totalXP}
          onPickSubject={() => setScreen('subjects')}
          onReset={() => setScreen('welcome')}
        />
      )}
    </div>
  );
}
